<script setup>
import { computed, watch, ref, onMounted } from 'vue'

import { useStore } from 'vuex'

import StreamPlayer from '../ui/StreamPlayer.vue';
import CountDown from '../ui/CountDown.vue';
import Spinner from '../ui/Spinner.vue';
import _ from 'lodash';
import StreamStatsService from '../../services/StreamStatsService';
import MonitoringChart from '@/views/StreamsDash/MonitoringChart.vue';

const props = defineProps({
  stream: {
    type: Object,
    required: true,
    default() { return {}; }
  },
  refLink: {
    type: Number,
    default: null,
    required: false
  },
  bitrateChart: {
    type: Boolean,
    default: false
  },
})

const store = useStore()

const emit = defineEmits({
	'on-source-stream-edit': (value) => { return value },
})

const scopeAlive = ref(true)
const mediaPulse = ref(null)
const mediaPulseTimeoutCtrl = ref(null)
const streamPreviewSpecs = ref({})

watch(() => mediaPulse.value, () => {
  if (mediaPulse.value.isWowza === true) {
    streamPreviewSpecs.value.type = 'hls';
    streamPreviewSpecs.value.url = `https://wowza-stage.castr.io/static/${props.stream.key}/playlist_sfm4s.m3u8`;
  }

  if (_.isEmpty(streamPreviewSpecs.value)) {
    streamPreviewSpecs.value.type = 'mse';
    streamPreviewSpecs.value.url = `wss://wowza-stage.castr.io/${props.stream.key}/mse_ld?tracks=v1a1`;
  }
})

const subscribeMediaPulse = async () => {
	(async function loop() {
		if (!scopeAlive.value) return;

		let timeoutInterval = 5000;
		let shouldUpdatePulse = props.stream.enabled;
		if (document.hidden) {
			shouldUpdatePulse = false;
			timeoutInterval = 6000;
		}

		if (shouldUpdatePulse) {
			if (!document.hasFocus()) {
				timeoutInterval = 6000;
			}
		}
		if (shouldUpdatePulse) {
			try {
				const streamKey = props.stream?.key;
				const pulseObject = await StreamStatsService.getStreamMediaPulse(
					streamKey
				);
				onMediaPulse(pulseObject);
			} catch (e) {
				console.log(e);
			}
		}
		mediaPulseTimeoutCtrl.value = setTimeout(loop, timeoutInterval);
	})();
}

const onMediaPulse = (pulseObject = {}) => {
	if (mediaPulse.value?.withoutPushStats) {
		const oldPushStats = mediaPulse.value.push_stats;
		pulseObject.push_stats = oldPushStats;
	}
	mediaPulse.value = pulseObject;
}

const unsubscribeMediaPulse = () => {
	clearTimeout(mediaPulseTimeoutCtrl.value);
}
onMounted(async () => {
	subscribeMediaPulse()
})

watch(() => props.stream, () => {
    if(!mediaPulseTimeoutCtrl.value && props.stream) {
        subscribeMediaPulse()
    }

    if (!props.stream === null) {
        unsubscribeMediaPulse();
    }
})

const onSourceStreamEdit = () => {
  emit('on-source-stream-edit', { stream: props.stream, refLink: props.refLink })
}

</script>

<template>
  <div v-if="stream?.enabled" class="w-full items-center align-center flex justify-center">
    <div v-if="mediaPulse?.alive" :class="
        {
          'w-full': true, 
          'items-center': bitrateChart,
          'align-center': bitrateChart,
          'justify-center': bitrateChart,
          'flex': bitrateChart,
        }">
        <stream-player v-if="!bitrateChart" :stream="stream" :stream-url="streamPreviewSpecs.url"
          :media-pulse="mediaPulse" :multiview="true" :refLink="refLink" class="video-placeholder bg-black position-relative" />

          <div v-else class="" :style="'width: 80%; margin-top: 30px;'">
				    <MonitoringChart :streamKey="stream.key" :streamAlive="mediaPulse?.alive" :isAllowed="true" legendPosition="top" :tooltip="false" />
          </div>

      <!-- <div v-else
        class="video-placeholder test-s-l text-500 text-dark-4 d-flex align-items-center justify-content-center flex-wrap bg-black mt-4">
        <p v-if="!shouldShowCounter" class="m-0">Waiting for stream</p>
        <div class="text-center" v-else-if="countdownSecsLeft > 0 && mediaPulse">
          TV Playout will go Live in <br>
          <CountDown :endDate="playoutStartTime">
          </CountDown>
        </div>
        <div v-else
          class="video-placeholder test-s-l text-500 text-dark-4 d-flex align-items-center justify-content-center flex-wrap bg-black">
          <Spinner text="Retrieving data..." classes="text-dark-4" spinner-color="var(--c-dark-8)" spinner-size="15px" />
        </div>
    </div> -->

      <div class="flex inline-flex self-center absolute top-2 z-10 left-3 items-center align-center justify-between w-full">
        <span class="text-sm font-medium leading-none rounded px-2 py-1 text-white">{{ ( stream.name || '').substr(0, 40) }}</span>
        <span class="mr-4">
          <a href="#" @click.prevent="onSourceStreamEdit">
            <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M14.4508 2.04937C13.9257 1.5243 13.0744 1.5243 12.5493 2.04937L12.3415 2.25715C12.3638 3.29454 13.2056 4.13639 14.243 4.15862L14.4508 3.95085C14.9759 3.42577 14.9758 2.57445 14.4508 2.04937ZM14.9269 5.17173L15.2993 4.79937C16.293 3.80567 16.293 2.19455 15.2993 1.20085C14.3056 0.20714 12.6945 0.207139 11.7008 1.20085L11.3283 1.57336C11.3264 1.57515 11.3247 1.57694 11.3229 1.57875L8.66221 4.23941L8.62239 4.27923L8.62238 4.27924C8.16327 4.73827 7.88045 5.02104 7.65156 5.34637C7.44866 5.63477 7.27959 5.94555 7.14768 6.27258C6.99888 6.64149 6.91512 7.03256 6.77914 7.66739L6.76734 7.72245L6.41336 9.37439C6.37071 9.5734 6.43186 9.78046 6.57577 9.92437C6.71969 10.0683 6.92675 10.1294 7.12575 10.0868L8.7777 9.7328L8.83276 9.72101C9.46759 9.58503 9.85866 9.50126 10.2276 9.35247C10.5546 9.22056 10.8654 9.05149 11.1538 8.84858C11.4791 8.61969 11.7619 8.33687 12.2209 7.87775L12.2607 7.83793L14.9212 5.17744C14.9232 5.17555 14.9251 5.17365 14.9269 5.17173ZM13.2259 5.17572C12.3433 4.85874 11.6414 4.15686 11.3244 3.27425L9.51074 5.08794C8.99955 5.59912 8.7951 5.80646 8.633 6.03687C8.48303 6.25003 8.35806 6.47974 8.26057 6.72145C8.15519 6.98272 8.09218 7.26701 7.94071 7.97389L7.78101 8.71914L8.52626 8.55944C9.23314 8.40797 9.51743 8.34496 9.77869 8.23958C10.0204 8.14209 10.2501 8.01712 10.4633 7.86715C10.6937 7.70505 10.901 7.50059 11.4122 6.98941L13.2259 5.17572ZM5.90019 2.90009L5.99993 2.90011C6.3313 2.90011 6.59993 3.16874 6.59993 3.50011C6.59993 3.83148 6.3313 4.10011 5.99993 4.10011C5.03133 4.10011 4.66473 4.10526 4.37877 4.18188C3.55054 4.40381 2.90363 5.05072 2.68171 5.87894C2.60508 6.16491 2.59993 6.53151 2.59993 7.50011V9.70011C2.59993 10.5501 2.6004 11.1515 2.63881 11.6216C2.67665 12.0848 2.74831 12.3675 2.86152 12.5897C3.09161 13.0413 3.45877 13.4084 3.91035 13.6385C4.13254 13.7517 4.41524 13.8234 4.8784 13.8612C5.34855 13.8996 5.94995 13.9001 6.79993 13.9001H8.99993C9.96853 13.9001 10.3351 13.895 10.6211 13.8183C11.4493 13.5964 12.0962 12.9495 12.3182 12.1213C12.3948 11.8353 12.3999 11.4687 12.3999 10.5001C12.3999 10.1687 12.6686 9.90011 12.9999 9.90011C13.3313 9.90011 13.5999 10.1687 13.5999 10.5001L13.5999 10.5998V10.5999C13.6001 11.4339 13.6003 11.9729 13.4773 12.4319C13.1444 13.6742 12.174 14.6446 10.9317 14.9774C10.4727 15.1004 9.93377 15.1003 9.09968 15.1001H9.09966L8.99993 15.1001H6.79993H6.77369C5.95586 15.1001 5.30543 15.1001 4.78068 15.0572C4.24332 15.0133 3.78511 14.9215 3.36557 14.7077C2.68818 14.3626 2.13745 13.8119 1.79231 13.1345C1.57854 12.7149 1.4867 12.2567 1.4428 11.7194C1.39992 11.1946 1.39993 10.5442 1.39993 9.72636L1.39993 9.70011V7.50011L1.39992 7.40036C1.39973 6.56627 1.39961 6.02735 1.5226 5.56836C1.85548 4.32603 2.82585 3.35566 4.06818 3.02277C4.52717 2.89979 5.0661 2.89991 5.90019 2.90009Z" fill="#FAFAFB"/>
            </svg>
          </a>
        </span>
      </div>
    </div>
    <div v-else class="w-full">
      <div 
        class="w-full h-80 test-s-l text-500 text-dark-4 d-flex align-items-center justify-content-center flex-wrap bg-darker">
        <p class="text-dark-7 text-normal text-center font-medium w-full">
          Stream offline
        </p>
      </div>

      <div class="flex inline-flex self-center absolute top-2 z-10 left-3 items-center align-center justify-between w-full">
        <span class="text-sm font-medium leading-none rounded px-2 py-1 text-white">{{ ( stream.name || '').substr(0, 40) }}</span>
        <span class="mr-4">
          <a href="#" @click.prevent="onSourceStreamEdit">
            <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M14.4508 2.04937C13.9257 1.5243 13.0744 1.5243 12.5493 2.04937L12.3415 2.25715C12.3638 3.29454 13.2056 4.13639 14.243 4.15862L14.4508 3.95085C14.9759 3.42577 14.9758 2.57445 14.4508 2.04937ZM14.9269 5.17173L15.2993 4.79937C16.293 3.80567 16.293 2.19455 15.2993 1.20085C14.3056 0.20714 12.6945 0.207139 11.7008 1.20085L11.3283 1.57336C11.3264 1.57515 11.3247 1.57694 11.3229 1.57875L8.66221 4.23941L8.62239 4.27923L8.62238 4.27924C8.16327 4.73827 7.88045 5.02104 7.65156 5.34637C7.44866 5.63477 7.27959 5.94555 7.14768 6.27258C6.99888 6.64149 6.91512 7.03256 6.77914 7.66739L6.76734 7.72245L6.41336 9.37439C6.37071 9.5734 6.43186 9.78046 6.57577 9.92437C6.71969 10.0683 6.92675 10.1294 7.12575 10.0868L8.7777 9.7328L8.83276 9.72101C9.46759 9.58503 9.85866 9.50126 10.2276 9.35247C10.5546 9.22056 10.8654 9.05149 11.1538 8.84858C11.4791 8.61969 11.7619 8.33687 12.2209 7.87775L12.2607 7.83793L14.9212 5.17744C14.9232 5.17555 14.9251 5.17365 14.9269 5.17173ZM13.2259 5.17572C12.3433 4.85874 11.6414 4.15686 11.3244 3.27425L9.51074 5.08794C8.99955 5.59912 8.7951 5.80646 8.633 6.03687C8.48303 6.25003 8.35806 6.47974 8.26057 6.72145C8.15519 6.98272 8.09218 7.26701 7.94071 7.97389L7.78101 8.71914L8.52626 8.55944C9.23314 8.40797 9.51743 8.34496 9.77869 8.23958C10.0204 8.14209 10.2501 8.01712 10.4633 7.86715C10.6937 7.70505 10.901 7.50059 11.4122 6.98941L13.2259 5.17572ZM5.90019 2.90009L5.99993 2.90011C6.3313 2.90011 6.59993 3.16874 6.59993 3.50011C6.59993 3.83148 6.3313 4.10011 5.99993 4.10011C5.03133 4.10011 4.66473 4.10526 4.37877 4.18188C3.55054 4.40381 2.90363 5.05072 2.68171 5.87894C2.60508 6.16491 2.59993 6.53151 2.59993 7.50011V9.70011C2.59993 10.5501 2.6004 11.1515 2.63881 11.6216C2.67665 12.0848 2.74831 12.3675 2.86152 12.5897C3.09161 13.0413 3.45877 13.4084 3.91035 13.6385C4.13254 13.7517 4.41524 13.8234 4.8784 13.8612C5.34855 13.8996 5.94995 13.9001 6.79993 13.9001H8.99993C9.96853 13.9001 10.3351 13.895 10.6211 13.8183C11.4493 13.5964 12.0962 12.9495 12.3182 12.1213C12.3948 11.8353 12.3999 11.4687 12.3999 10.5001C12.3999 10.1687 12.6686 9.90011 12.9999 9.90011C13.3313 9.90011 13.5999 10.1687 13.5999 10.5001L13.5999 10.5998V10.5999C13.6001 11.4339 13.6003 11.9729 13.4773 12.4319C13.1444 13.6742 12.174 14.6446 10.9317 14.9774C10.4727 15.1004 9.93377 15.1003 9.09968 15.1001H9.09966L8.99993 15.1001H6.79993H6.77369C5.95586 15.1001 5.30543 15.1001 4.78068 15.0572C4.24332 15.0133 3.78511 14.9215 3.36557 14.7077C2.68818 14.3626 2.13745 13.8119 1.79231 13.1345C1.57854 12.7149 1.4867 12.2567 1.4428 11.7194C1.39992 11.1946 1.39993 10.5442 1.39993 9.72636L1.39993 9.70011V7.50011L1.39992 7.40036C1.39973 6.56627 1.39961 6.02735 1.5226 5.56836C1.85548 4.32603 2.82585 3.35566 4.06818 3.02277C4.52717 2.89979 5.0661 2.89991 5.90019 2.90009Z" fill="#FAFAFB"/>
            </svg>
          </a>
        </span>
      </div>
    </div>
  </div>
</template>
<style scoped></style>
